import EventEmitter from 'eventemitter3';

export type WSocketConnectionState =
    | 'idle'
    | 'connecting'
    | 'connected'
    | 'error';
interface WSocketEvents {
    connectionState: (state: WSocketConnectionState) => void;
    message: (event: MessageEvent) => void;
}
interface WSocketOpts {
    maxConnectionAttempts: number;
    reconnectDelay: number;
}
/**
 * A wrapper arround WebSocket to make it easier to use with automatic support
 * for reconnections
 */
export class WSocket extends EventEmitter<WSocketEvents> {
    private connectionState: WSocketConnectionState = 'idle';
    private socket: WebSocket | null = null;

    private connectionAttempt: number = 0;
    private opts: WSocketOpts;
    private reconnectHandle: ReturnType<typeof setTimeout> | undefined;

    constructor(
        public url: string,
        opts: Partial<WSocketOpts> = {},
    ) {
        super();
        this.opts = {
            ...opts,
            maxConnectionAttempts: 10,
            reconnectDelay: 1000,
        };
    }

    private setConnectionState = (state: WSocketConnectionState) => {
        this.connectionState = state;
        this.emit('connectionState', this.connectionState);
    };

    public connect = () => {
        if (this.socket) {
            this.socket.removeEventListener('open', this.onOpen);
            this.socket.removeEventListener('close', this.onClose);
            this.socket.removeEventListener('error', this.onError);
            this.socket.removeEventListener('message', this.onMessage);
        }

        this.setConnectionState('connecting');

        this.socket = new WebSocket(this.url);
        this.connectionAttempt += 1;
        this.socket.addEventListener('open', this.onOpen);
        this.socket.addEventListener('close', this.onClose);
        this.socket.addEventListener('error', this.onError);
        this.socket.addEventListener('message', this.onMessage);
    };

    public sendMessage = (data: unknown) => {
        if (this.socket && this.connectionState === 'connected') {
            this.socket.send(JSON.stringify(data));
        }
    };

    public disconnect = () => {
        if (this.reconnectHandle) {
            clearTimeout(this.reconnectHandle);
        }
        this.setConnectionState('idle');
        this.socket?.close();
        this.socket = null;
    };

    private reconnect = () => {
        if (this.reconnectHandle) {
            clearTimeout(this.reconnectHandle);
        }

        this.setConnectionState('connecting');
        this.reconnectHandle = setTimeout(() => {
            this.connect();
        }, this.opts.reconnectDelay);
    };

    public getConnectionState = () => {
        return this.connectionState;
    };

    private onMessage = (e: MessageEvent) => {
        this.emit('message', e);
    };

    private onOpen = () => {
        this.connectionAttempt = 0;
        this.setConnectionState('connected');
    };

    private onClose = () => {
        this.socket = null;
        if (this.connectionState === 'connected') {
            this.setConnectionState('idle');
        }
    };

    private onError = (e: Event) => {
        this.socket = null;
        console.error('WebSocket encountered error, reconnecting...', e);
        if (
            this.connectionState !== 'idle' &&
            this.connectionAttempt < this.opts.maxConnectionAttempts
        ) {
            this.reconnect();
        } else {
            if (this.reconnectHandle) {
                clearTimeout(this.reconnectHandle);
            }
            this.setConnectionState('error');
        }
    };
}
